import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import io from 'socket.io-client';
import toast from 'react-hot-toast';
import { gigSwal } from '../utils/swal';
import { Download, Printer, Mic, Settings, ExternalLink, Copy } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';

const API_URL = process.env.REACT_APP_API_URL || '';

function RequestsDashboard() {
  const theme = useTheme();
  const [requests, setRequests] = useState([]);
  const [clearing, setClearing] = useState(false);
  const [requestLimit, setRequestLimit] = useState(1);
  const [activeGig, setActiveGig] = useState(null);
  const [isAcceptingRequests, setIsAcceptingRequests] = useState(false);
  const [toggling, setToggling] = useState(false);
  const [gigUrl, setGigUrl] = useState('demo-gig-2024');
  const [bandSlug, setBandSlug] = useState('');
  const [teleprompter, setTeleprompter] = useState(null);
  const [teleprompterTab, setTeleprompterTab] = useState('chords');
  const [sheetOpen, setSheetOpen] = useState(false);
  const [sheetY, setSheetY] = useState(0);
  const dragStart = useRef(null);

  useEffect(() => {
    fetchActiveGig();
    axios.get(`${API_URL}/api/settings`).then(res => setBandSlug(res.data.band_slug || '')).catch(() => {});
  }, []);

  useEffect(() => {
    if (!gigUrl) return;
    fetchRequests();
    fetchGigSettings();
    const socket = io(API_URL);
    socket.emit('joinGig', gigUrl);
    socket.on('newRequest', () => fetchRequests());
    socket.on('requestsCleared', () => setRequests([]));
    socket.on('requestStatusChanged', (data) => setIsAcceptingRequests(data.is_accepting_requests));
    return () => socket.disconnect();
  }, [gigUrl]);

  const fetchActiveGig = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/gigs/active`);
      setActiveGig(response.data);
      setGigUrl(response.data.unique_url);
      setIsAcceptingRequests(response.data.is_accepting_requests || false);
    } catch (err) {
      console.error('Error fetching active gig:', err);
    }
  };

  const fetchRequests = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/requests/gig/${gigUrl}`);
      setRequests(response.data);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchGigSettings = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/gigs/${gigUrl}/settings`);
      setRequestLimit(response.data.max_requests_per_device || 1);
    } catch (err) {
      console.error('Error fetching gig settings:', err);
    }
  };

  const handleClearRequests = async () => {
    setSheetOpen(false);
    const result = await gigSwal(theme, {
      title: 'Clear All Requests?',
      text: 'This will remove all pending requests. This cannot be undone.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc3545',
      confirmButtonText: 'Yes, clear all',
      cancelButtonText: 'Cancel',
      reverseButtons: true,
    });
    if (!result.isConfirmed) return;
    setClearing(true);
    try {
      await axios.post(`${API_URL}/api/requests/gig/${gigUrl}/clear`);
      setRequests([]);
      toast.success('All requests cleared');
    } catch (err) {
      console.error(err);
      toast.error('Failed to clear requests');
    } finally {
      setClearing(false);
    }
  };

  const handleResetDeviceLimits = async () => {
    const result = await gigSwal(theme, {
      title: 'Reset Device Limits?',
      text: 'This will allow all devices to request songs again.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, reset limits',
      cancelButtonText: 'Cancel',
      reverseButtons: true,
    });
    if (!result.isConfirmed) return;
    try {
      await axios.post(`${API_URL}/api/gigs/${gigUrl}/reset-devices`);
      toast.success('Device limits reset');
    } catch (err) {
      console.error(err);
      toast.error('Failed to reset device limits');
    }
  };

  const handleUpdateRequestLimit = async (value) => {
    setRequestLimit(value);
    try {
      await axios.patch(`${API_URL}/api/gigs/${gigUrl}/settings`, { max_requests_per_device: parseInt(value) });
      toast.success('Request limit updated');
    } catch (err) {
      console.error(err);
      toast.error('Failed to update request limit');
    }
  };

  const handleCompleteSong = async (songId) => {
    try {
      await axios.patch(`${API_URL}/api/requests/song/${songId}/complete-all`, { gigUrl });
      fetchRequests();
      toast.success('Song marked as played');
    } catch (err) {
      console.error(err);
      toast.error('Failed to complete requests');
    }
  };

  const handleToggleRequests = async () => {
    if (!activeGig) return;
    setToggling(true);
    try {
      const response = await axios.patch(`${API_URL}/api/gigs/${activeGig.id}/toggle-requests`);
      setIsAcceptingRequests(response.data.is_accepting_requests);
      toast.success(response.data.is_accepting_requests ? 'Now accepting requests' : 'Requests closed');
    } catch (err) {
      console.error(err);
      toast.error('Failed to toggle request status');
    } finally {
      setToggling(false);
    }
  };

  const openTeleprompter = (request) => {
    setTeleprompterTab(request.chords ? 'chords' : 'lyrics');
    setTeleprompter(request);
  };

  // Bottom sheet drag-to-dismiss
  const onTouchStart = e => { dragStart.current = e.touches[0].clientY; };
  const onTouchMove  = e => { const dy = e.touches[0].clientY - dragStart.current; if (dy > 0) setSheetY(dy); };
  const onTouchEnd   = () => { if (sheetY > 80) setSheetOpen(false); setSheetY(0); };

  const audienceUrl = bandSlug ? `${window.location.origin}/gig/${bandSlug}` : '';

  // Status strip colours (theme-aware)
  const accepting = isAcceptingRequests;
  const stripBg     = accepting ? (theme.isDark ? '#0a2318' : '#d4edda') : (theme.isDark ? '#220a0a' : '#f8d7da');
  const stripBorder = accepting ? (theme.isDark ? '#1a4a30' : '#c3e6cb') : (theme.isDark ? '#4a1a1a' : '#f5c6cb');
  const stripText   = accepting ? (theme.isDark ? '#2ecc71' : '#155724') : (theme.isDark ? '#e74c3c' : '#721c24');
  const dotColor    = accepting ? '#2ecc71' : '#e74c3c';
  const closeBtnBg  = accepting ? '#dc3545' : '#28a745';

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden', background: theme.background }}>

      {/* ── Teleprompter overlay (full-screen, unchanged) ───── */}
      {teleprompter && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 2000, background: '#0d0d0d', display: 'flex', flexDirection: 'column' }}>
          <div style={{ flexShrink: 0, padding: '0.75rem 1rem', background: '#1a1a1a', borderBottom: '1px solid #333', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            {teleprompter.chords && teleprompter.lyrics && (
              <div style={{ display: 'flex', gap: '0.25rem' }}>
                {[['chords', 'Chords'], ['lyrics', 'Lyrics']].map(([key, label]) => (
                  <button key={key} onClick={() => setTeleprompterTab(key)} style={{
                    padding: '0.35rem 0.85rem', border: 'none', borderRadius: '6px', cursor: 'pointer',
                    fontSize: '0.85rem', fontWeight: '600',
                    background: teleprompterTab === key ? theme.primary : '#333',
                    color: teleprompterTab === key ? theme.onPrimary : '#aaa',
                  }}>
                    {label}
                  </button>
                ))}
              </div>
            )}
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: '700', color: 'white', fontSize: '0.95rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {teleprompter.title}
              </div>
              <div style={{ fontSize: '0.78rem', color: '#888', marginTop: '1px' }}>{teleprompter.artist_original}</div>
            </div>
            <button onClick={() => setTeleprompter(null)} style={{
              background: '#333', border: 'none', borderRadius: '8px', color: 'white',
              padding: '0.45rem 0.9rem', cursor: 'pointer', fontSize: '0.9rem', fontWeight: '600', flexShrink: 0,
            }}>
              ✕ Close
            </button>
          </div>
          <div style={{ flex: 1, overflowY: 'auto', padding: '1.25rem 1rem' }}>
            {teleprompterTab === 'chords' && teleprompter.chords && (
              <pre style={{ color: '#e8e8e8', fontFamily: 'monospace', fontSize: '1.05rem', lineHeight: 1.9, whiteSpace: 'pre-wrap', wordBreak: 'break-word', margin: 0 }}>
                {teleprompter.chords}
              </pre>
            )}
            {teleprompterTab === 'lyrics' && teleprompter.lyrics && (
              <pre style={{ color: '#e8e8e8', fontFamily: "'Helvetica Neue', Arial, sans-serif", fontSize: '1.1rem', lineHeight: 2, whiteSpace: 'pre-wrap', wordBreak: 'break-word', margin: 0 }}>
                {teleprompter.lyrics}
              </pre>
            )}
            {teleprompterTab === 'chords' && !teleprompter.chords && teleprompter.lyrics && (
              <pre style={{ color: '#e8e8e8', fontFamily: 'sans-serif', fontSize: '1.1rem', lineHeight: 2, whiteSpace: 'pre-wrap', margin: 0 }}>
                {teleprompter.lyrics}
              </pre>
            )}
          </div>
        </div>
      )}

      {/* ── Status strip (pinned) ────────────────────────────── */}
      <div style={{
        flexShrink: 0,
        background: activeGig ? stripBg : theme.surfaceAlt,
        borderBottom: `1px solid ${activeGig ? stripBorder : theme.border}`,
        padding: '10px 14px',
        display: 'flex', alignItems: 'center', gap: 10,
        transition: 'background 0.4s, border-color 0.4s',
      }}>
        {/* Pulsing dot */}
        <div style={{
          width: 10, height: 10, borderRadius: '50%', flexShrink: 0,
          background: activeGig ? dotColor : theme.textMuted,
          boxShadow: activeGig ? `0 0 0 3px ${dotColor}30` : 'none',
          transition: 'background 0.3s, box-shadow 0.3s',
        }} />

        {/* Status text + gig info */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 700, fontSize: 12, letterSpacing: 0.5, color: activeGig ? stripText : theme.textMuted, transition: 'color 0.3s' }}>
            {!activeGig ? 'NO ACTIVE GIG' : accepting ? 'ACCEPTING REQUESTS' : 'REQUESTS CLOSED'}
          </div>
          {activeGig && (
            <div style={{ color: theme.textSecondary, fontSize: 11, marginTop: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {activeGig.gig_name} · {activeGig.venue_name}
            </div>
          )}
        </div>

        {/* Open/Close toggle */}
        {activeGig && (
          <button
            onClick={handleToggleRequests}
            disabled={toggling}
            style={{
              background: closeBtnBg, color: '#fff', border: 'none', borderRadius: 8,
              padding: '8px 14px', fontWeight: 700, fontSize: 12, cursor: toggling ? 'not-allowed' : 'pointer',
              flexShrink: 0, minHeight: 36, opacity: toggling ? 0.6 : 1,
              WebkitTapHighlightColor: 'transparent', transition: 'background 0.2s',
            }}
          >
            {toggling ? '...' : accepting ? 'Close' : 'Open'}
          </button>
        )}

        {/* Cog → sheet */}
        {activeGig && (
          <button
            onClick={() => setSheetOpen(true)}
            style={{
              background: theme.primary, border: 'none', borderRadius: 8,
              width: 36, height: 36,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              cursor: 'pointer', flexShrink: 0,
              WebkitTapHighlightColor: 'transparent',
            }}
          >
            <Settings size={17} color={theme.onPrimary} />
          </button>
        )}
      </div>

      {/* ── Request list (scrollable) ────────────────────────── */}
      <div style={{ flex: 1, overflowY: 'auto', WebkitOverflowScrolling: 'touch', padding: '8px 12px 12px', display: 'flex', flexDirection: 'column', gap: 6 }}>

        <div style={{ color: theme.textMuted, fontSize: 11, fontFamily: 'monospace', marginBottom: 2, padding: '0 2px' }}>
          {requests.length} pending
        </div>

        {requests.length === 0 ? (
          <div style={{
            color: theme.textMuted, fontSize: 13, textAlign: 'center', marginTop: 48,
            padding: '2rem', background: theme.surfaceAlt, borderRadius: 12,
          }}>
            {activeGig ? 'No requests yet. Share the QR code to start!' : 'Set a gig as active to receive requests.'}
          </div>
        ) : (
          requests.map((request) => (
            <div
              key={request.song_id}
              style={{
                background: theme.surface,
                border: `1px solid ${theme.border}`,
                borderRadius: 10,
                padding: '12px 14px',
                display: 'flex', alignItems: 'center', gap: 10,
              }}
            >
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 2 }}>
                  <div style={{
                    color: theme.text, fontWeight: 700, fontSize: 14,
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  }}>
                    {request.title}
                  </div>
                  {request.request_count > 1 && (
                    <span style={{
                      background: '#dc3545', color: '#fff',
                      padding: '1px 7px', borderRadius: 12,
                      fontSize: 11, fontWeight: 700, flexShrink: 0,
                    }}>
                      {request.request_count}
                    </span>
                  )}
                </div>
                <div style={{ color: theme.textSecondary, fontSize: 12, marginBottom: 2 }}>{request.artist_original}</div>
                <div style={{ color: theme.textMuted, fontSize: 11, fontFamily: 'monospace', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  — {request.requesters.map(r => r.requester_name).join(', ')}
                </div>
              </div>

              <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                {(request.chords || request.lyrics) && (
                  <button
                    onClick={() => openTeleprompter(request)}
                    title="Open teleprompter"
                    style={{
                      background: theme.surfaceAlt, color: theme.textSecondary,
                      border: `1px solid ${theme.border}`, borderRadius: 8,
                      padding: '9px 10px', cursor: 'pointer',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      minHeight: 40, WebkitTapHighlightColor: 'transparent',
                    }}
                  >
                    <Mic size={14} />
                  </button>
                )}
                <button
                  onClick={() => handleCompleteSong(request.song_id)}
                  style={{
                    background: theme.primary, color: theme.onPrimary,
                    border: 'none', borderRadius: 8,
                    padding: '9px 14px', fontWeight: 700,
                    fontSize: 12, cursor: 'pointer',
                    whiteSpace: 'nowrap', minHeight: 40,
                    WebkitTapHighlightColor: 'transparent',
                  }}
                >
                  ✓ Played
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {/* ── Setup bottom sheet ───────────────────────────────── */}
      {sheetOpen && (
        <>
          {/* Scrim */}
          <div
            onClick={() => setSheetOpen(false)}
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 100 }}
          />

          {/* Sheet */}
          <div
            onTouchStart={onTouchStart}
            onTouchMove={onTouchMove}
            onTouchEnd={onTouchEnd}
            style={{
              position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 101,
              background: theme.surface,
              borderTop: `2px solid ${theme.primary}`,
              borderRadius: '20px 20px 0 0',
              padding: '0 16px 28px',
              transform: `translateY(${sheetY}px)`,
              transition: sheetY === 0 ? 'transform 0.3s cubic-bezier(0.32,0.72,0,1)' : 'none',
              boxShadow: '0 -8px 40px rgba(0,0,0,0.3)',
              maxHeight: '85vh', overflowY: 'auto',
            }}
          >
            {/* Drag handle */}
            <div style={{ width: 40, height: 4, background: theme.border, borderRadius: 2, margin: '12px auto 20px' }} />

            {/* Sheet header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <div style={{ color: theme.text, fontWeight: 700, fontSize: 18 }}>Gig Setup</div>
              <button
                onClick={() => setSheetOpen(false)}
                style={{ background: theme.surfaceAlt, border: 'none', color: theme.textSecondary, borderRadius: '50%', width: 32, height: 32, fontSize: 18, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', WebkitTapHighlightColor: 'transparent' }}
              >×</button>
            </div>

            {/* QR section */}
            {activeGig && bandSlug && (
              <>
                <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', marginBottom: 10 }}>
                  <img
                    src={`${API_URL}/api/band/${bandSlug}/qr`}
                    alt="QR code"
                    style={{ width: 88, height: 88, borderRadius: 8, flexShrink: 0, background: '#fff' }}
                  />
                  <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <a
                      href={`${API_URL}/api/band/${bandSlug}/qr`}
                      download={`qr-${bandSlug}.png`}
                      style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                        background: theme.primary, color: theme.onPrimary, textDecoration: 'none',
                        borderRadius: 9, padding: '12px 0', fontWeight: 700, fontSize: 13,
                        WebkitTapHighlightColor: 'transparent',
                      }}
                    >
                      <Download size={14} /> Download QR
                    </a>
                    <a
                      href={`/print/gig/${activeGig.unique_url}`}
                      target="_blank"
                      rel="noreferrer"
                      style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                        background: theme.primary, color: theme.onPrimary, textDecoration: 'none',
                        borderRadius: 9, padding: '12px 0', fontWeight: 700, fontSize: 13,
                        WebkitTapHighlightColor: 'transparent',
                      }}
                    >
                      <Printer size={14} /> Print Promo
                    </a>
                  </div>
                </div>
                <div style={{ color: theme.primary, fontSize: 10, fontFamily: 'monospace', opacity: 0.7, marginBottom: 14, wordBreak: 'break-all' }}>
                  {audienceUrl}
                </div>

                {/* Band view link */}
                <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
                  <a
                    href="/band"
                    target="_blank"
                    rel="noreferrer"
                    style={{
                      flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                      background: theme.surfaceAlt, color: theme.text, textDecoration: 'none',
                      borderRadius: 9, padding: '10px 0', fontWeight: 600, fontSize: 13,
                      border: `1px solid ${theme.border}`, WebkitTapHighlightColor: 'transparent',
                    }}
                  >
                    <ExternalLink size={13} /> Band view
                  </a>
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(window.location.origin + '/band');
                      toast.success('Link copied!');
                    }}
                    style={{
                      padding: '10px 14px', background: theme.surfaceAlt, color: theme.textSecondary,
                      border: `1px solid ${theme.border}`, borderRadius: 9, cursor: 'pointer',
                      display: 'flex', alignItems: 'center', WebkitTapHighlightColor: 'transparent',
                    }}
                    title="Copy band view link"
                  >
                    <Copy size={13} />
                  </button>
                </div>
              </>
            )}

            <div style={{ height: 1, background: theme.border, marginBottom: 18 }} />

            {/* Max requests control */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
              <span style={{ color: theme.textSecondary, fontSize: 13, flex: 1 }}>Max requests per device</span>
              <select
                value={requestLimit}
                onChange={e => handleUpdateRequestLimit(e.target.value)}
                style={{
                  background: theme.surfaceAlt, border: `1px solid ${theme.border}`,
                  borderRadius: 8, padding: '8px 10px', color: theme.text,
                  fontSize: 13, cursor: 'pointer',
                }}
              >
                {[1, 2, 3, 4, 5].map(n => <option key={n} value={n}>{n}</option>)}
              </select>
              <button
                onClick={handleResetDeviceLimits}
                style={{
                  background: theme.surfaceAlt, border: `1px solid ${theme.border}`,
                  borderRadius: 8, padding: '8px 14px', color: theme.textSecondary,
                  fontSize: 13, cursor: 'pointer', WebkitTapHighlightColor: 'transparent',
                }}
              >
                Reset
              </button>
            </div>

            {/* Clear all */}
            <button
              onClick={handleClearRequests}
              disabled={clearing || requests.length === 0}
              style={{
                width: '100%', background: 'transparent',
                border: '1px solid rgba(220,53,69,0.5)', borderRadius: 9,
                padding: '12px 0', color: clearing || requests.length === 0 ? theme.textMuted : '#dc3545',
                fontSize: 13, fontWeight: 600, cursor: clearing || requests.length === 0 ? 'not-allowed' : 'pointer',
                marginBottom: 14, WebkitTapHighlightColor: 'transparent',
              }}
            >
              {clearing ? 'Clearing...' : '🗑 Clear All Requests'}
            </button>

            {/* Done */}
            <button
              onClick={() => setSheetOpen(false)}
              style={{
                width: '100%', background: theme.primary, color: theme.onPrimary,
                border: 'none', borderRadius: 10, padding: '14px 0',
                fontWeight: 700, fontSize: 15, cursor: 'pointer',
                WebkitTapHighlightColor: 'transparent',
              }}
            >
              Done
            </button>
          </div>
        </>
      )}
    </div>
  );
}

export default RequestsDashboard;
